import React, { useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { useAuth } from "../lib/auth";
import { toast } from "sonner";
import { Lightning } from "@phosphor-icons/react";

/**
 * Google OAuth callback. The user lands on
 *   `${window.location.origin}/dashboard#session_id=xxx`
 * after signing in on Emergent's hosted screen. We detect the fragment
 * synchronously in App.js and mount this component to exchange the
 * session_id for our normal JWT, then bounce to /dashboard.
 * REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH.
 */
export default function AuthCallback() {
  const { loginWithToken } = useAuth();
  const nav = useNavigate();
  const done = useRef(false);

  useEffect(() => {
    if (done.current) return;
    done.current = true;
    const hash = window.location.hash || "";
    const m = hash.match(/session_id=([^&]+)/);
    const sessionId = m ? decodeURIComponent(m[1]) : "";
    // Clean the hash so a refresh doesn't loop
    if (window.history?.replaceState) {
      window.history.replaceState({}, "", window.location.pathname);
    }
    if (!sessionId) {
      toast.error("Google sign-in failed — missing session");
      nav("/login", { replace: true });
      return;
    }
    (async () => {
      try {
        const { data } = await api.post("/auth/google/session", { session_id: sessionId });
        loginWithToken(data.token, data.user);
        toast.success(data.new_user ? "Welcome! Your 14-day trial has started." : "Welcome back");
        nav("/dashboard", { replace: true });
      } catch (e) {
        toast.error(e?.response?.data?.detail || "Google sign-in failed");
        nav("/login", { replace: true });
      }
    })();
  }, [loginWithToken, nav]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-background" data-testid="auth-callback">
      <div className="flex flex-col items-center gap-3 text-muted-foreground">
        <Lightning size={28} weight="fill" className="text-primary animate-pulse" />
        <p className="text-sm">Finishing Google sign-in…</p>
      </div>
    </div>
  );
}
